import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { auditLogsService } from "../../services/audit-log.service";
import {
  AuditLogFilterOptions,
  AuditLogPageOptionsDto,
} from "../../dtos/audit-log.dto";
import { AuditLog } from "../../interfaces/audit-log.interface";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import DataTable, {
  Column,
  DataTableFetchParams,
  DataTableFilterDef,
} from "../../components/ui/DataTable";

const AuditLogsList = () => {
  const { t } = useTranslation("common");
  // Options come from the DB, so new actions/tables appear without a CMS change.
  const [filterOptions, setFilterOptions] = useState<AuditLogFilterOptions>({
    actions: [],
    tableNames: [],
  });

  useEffect(() => {
    auditLogsService
      .filterOptions()
      .then(setFilterOptions)
      .catch((error) =>
        console.error("Failed to fetch audit log filter options:", error),
      );
  }, []);

  const toOptions = (values: string[]) =>
    values.map((value) => ({ label: value, value }));

  const columns: Column<AuditLog>[] = [
    {
      key: "createdAt",
      header: "Date",
      render: (log) => new Date(log.createdAt).toLocaleString(),
      sortable: true,
    },
    {
      key: "action",
      header: "Action",
      className: "font-medium text-gray-900 dark:text-gray-100",
      sortable: true,
    },
    {
      key: "tableName",
      header: "Table",
      sortable: true,
    },
    {
      key: "userId",
      header: "User ID",
      render: (log) => log.userId || "-",
      sortable: true,
    },
    {
      key: "ipAddress",
      header: "IP Address",
      render: (log) => log.ipAddress || "-",
    },
    {
      key: "changes",
      header: "Changes",
      render: (log) => (
        <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded max-w-xs overflow-auto">
          {JSON.stringify(
            {
              recordId: log.recordId,
              oldValue: log.oldValue,
              newValue: log.newValue,
            },
            null,
            2,
          )}
        </pre>
      ),
    },
  ];

  const filterDefs: DataTableFilterDef[] = [
    {
      key: "action",
      label: "Action",
      type: "multiselect",
      options: toOptions(filterOptions.actions),
    },
    {
      key: "tableName",
      label: "Table",
      type: "multiselect",
      options: toOptions(filterOptions.tableNames),
    },
    {
      key: "userId",
      label: "User ID",
      type: "text",
      placeholder: "Filter by user ID...",
    },
  ];

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    try {
      const apiParams: AuditLogPageOptionsDto = {
        page: params.page,
        take: params.pageSize,
        orderBy: params.sortBy || "createdAt",
        order: params.sortOrder || "DESC",
        search: params.search,
        action: params.filters?.action?.length
          ? params.filters.action
          : undefined,
        tableName: params.filters?.tableName?.length
          ? params.filters.tableName
          : undefined,
        userId: params.filters?.userId,
      };

      const response = await auditLogsService.list(apiParams);
      return {
        data: response.data,
        total: response.meta.itemCount,
        totalPages: response.meta.pageCount,
      };
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      throw error;
    }
  }, []);

  return (
    <Layout title={t("audit_logs")}>
      <div className="space-y-6 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("audit_logs")}
            </h1>
          </div>
        </Card>

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          onFetch={handleFetch}
          filterDefs={filterDefs}
          initialState={{
            sortBy: "createdAt",
            sortOrder: "DESC",
            pageSize: 50,
          }}
          emptyMessage="No audit logs found"
        />
      </div>
    </Layout>
  );
};

export default AuditLogsList;
